import { useState } from 'react'

/**
 * IdentityFlagReasonModal — 5-option structured picker for "Identity requires
 * verification".
 *
 * When a clinician suspects the record needs re-verification (wrong photo,
 * wrong chart, duplicate suspected, name mismatch, etc.) they tap the
 * flag action and this modal captures the reason. Backend then moves the
 * record to `needs_review` state with the reason recorded for the audit
 * trail — see patients/views.py :: flag_identity.
 *
 * Deliberately reason-first (not "photo doesn't match") because the failure
 * modes are broader than a photo problem: sibling using account, phone
 * handed to family member, employer enrolled wrong person, etc. Structured
 * enum instead of free text so we can visualise trends later without NLP.
 */
const REASONS = [
  { value: 'photo_outdated',      label: 'Photo outdated',       hint: 'The photo is real but the person has aged / changed appearance.' },
  { value: 'photo_mismatch',      label: 'Photo mismatch',       hint: 'The person in front of me does not look like the photo.' },
  { value: 'duplicate_suspected', label: 'Duplicate suspected',  hint: 'I think this is the same person as another record on the platform.' },
  { value: 'name_discrepancy',    label: 'Name discrepancy',     hint: 'The name on the record does not match what the person is telling me.' },
  { value: 'other',               label: 'Other',                hint: 'A different concern about identity that does not fit the categories above.' },
]

export default function IdentityFlagReasonModal({
  patientName = '',
  isSubmitting = false,
  onSubmit,
  onCancel,
}) {
  const [reason, setReason] = useState('')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flag-identity-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-100">
          <h2 id="flag-identity-title" className="text-lg font-semibold text-slate-900">
            Identity requires verification
          </h2>
          <p className="text-xs text-slate-500 mt-1 leading-snug">
            Choose the closest reason. The record will move to <b>Needs review</b>{' '}
            and prompt the next clinician to re-verify. Any previous verifier is
            preserved in the audit trail.
            {patientName && (
              <>
                {' '}Patient: <span className="font-medium text-slate-700">{patientName}</span>.
              </>
            )}
          </p>
        </div>

        <form
          className="p-4 space-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (reason && onSubmit) onSubmit(reason)
          }}
        >
          {REASONS.map((r) => (
            <label
              key={r.value}
              className={
                'block border rounded-md p-3 cursor-pointer transition-colors ' +
                (reason === r.value
                  ? 'border-wantok-500 bg-wantok-50/60'
                  : 'border-slate-200 hover:bg-slate-50')
              }
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="flag-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="mt-1 accent-wantok-700"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{r.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-snug">{r.hint}</div>
                </div>
              </div>
            </label>
          ))}

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 mt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!reason || isSubmitting}
              className="text-sm px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-medium"
            >
              {isSubmitting ? 'Flagging…' : 'Flag identity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
